import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "../../editableProps";
import "./style.scss";
import Toggle from "@builder/components/general/Toggle";
import SelectInput from "@builder/components/general/SelectInput";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { ReceiptNode } from "@common/build/types";
import { updateNodeProperty, updateReceiptSettings } from "@builder/store/slices/BuilderSlice/builderSlice";
import { ReceiptSettingsType } from "@builder/utils/constants";
import Input from "@builder/components/general/Input";

const ReceiptData: React.FC<EditableProps> = ({ parent }: EditableProps) => {
    const node = useAppSelector(getSelectedNode) as ReceiptNode;
    const dispatch = useAppDispatch();

    const settings = node.settings;

    const taxesOptions = {
        both: "For each item and cart",
        item: "For each item only",
        cart: "For cart only"
    };

    const discountsOptions = {
        both: "For each item and cart",
        item: "For each item only",
        cart: "For cart only"
    };

    const cartFeeOptions = {
        both: "For each item and cart",
        item: "For each item only",
        cart: "For cart only"
    };

    const handleToggle = (val: boolean, type?: ReceiptSettingsType) => {
        dispatch(
            updateReceiptSettings({
                type: type || "",
                setting: val
            })
        );
    };

    const getDropdownValue = (type: string) => {
        let itemSetting: keyof typeof node.settings;
        let cartSetting: keyof typeof node.settings;

        switch (type) {
            case ReceiptSettingsType.TAXES:
                itemSetting = ReceiptSettingsType.PRODUCT_TAXES;
                cartSetting = ReceiptSettingsType.CART_TAXES;
                break;
            case ReceiptSettingsType.DISCOUNT:
                itemSetting = ReceiptSettingsType.PRODUCT_DISCOUNT;
                cartSetting = ReceiptSettingsType.CART_DISCOUNT;
                break;
            case ReceiptSettingsType.FEE:
                itemSetting = ReceiptSettingsType.PRODUCT_FEE;
                cartSetting = ReceiptSettingsType.CART_FEE;
                break;
            default:
                return "";
        }

        const itemValue = node.settings[itemSetting];
        const cartValue = node.settings[cartSetting];

        if (itemValue && cartValue) {
            return "both";
        } else if (itemValue) {
            return "item";
        } else if (cartValue) {
            return "cart";
        } else {
            return "";
        }
    };

    const handleDropdown = (val: string, type: ReceiptSettingsType) => {
        let itemSetting, cartSetting;
        switch (type) {
            case ReceiptSettingsType.TAXES:
                itemSetting = ReceiptSettingsType.PRODUCT_TAXES;
                cartSetting = ReceiptSettingsType.CART_TAXES;
                break;
            case ReceiptSettingsType.DISCOUNT:
                itemSetting = ReceiptSettingsType.PRODUCT_DISCOUNT;
                cartSetting = ReceiptSettingsType.CART_DISCOUNT;
                break;
            case ReceiptSettingsType.FEE:
                itemSetting = ReceiptSettingsType.PRODUCT_FEE;
                cartSetting = ReceiptSettingsType.CART_FEE;
                break;
            default:
                return;
        }

        switch (val) {
            case "both":
                dispatch(
                    updateReceiptSettings({
                        type: itemSetting,
                        setting: true
                    })
                );
                dispatch(
                    updateReceiptSettings({
                        type: cartSetting,
                        setting: true
                    })
                );
                break;
            case "item":
                dispatch(
                    updateReceiptSettings({
                        type: itemSetting,
                        setting: true
                    })
                );
                dispatch(
                    updateReceiptSettings({
                        type: cartSetting,
                        setting: false
                    })
                );
                break;
            case "cart":
                dispatch(
                    updateReceiptSettings({
                        type: itemSetting,
                        setting: false
                    })
                );
                dispatch(
                    updateReceiptSettings({
                        type: cartSetting,
                        setting: true
                    })
                );
                break;
            default:
                break;
        }
    };

    return (
        <SidebarCollapse title="Receipt data" parent={parent}>
            <div className="receipt-data-options-wrapper">
                <Toggle label="Show values" checked={settings.showValues} callback={v => handleToggle(v, ReceiptSettingsType.VALUES)} />
                <Toggle label="Show variations" checked={settings.showVariations} callback={v => handleToggle(v, ReceiptSettingsType.VARIATIONS)} />
                <Toggle label="Show notes" checked={settings.showNotes} callback={v => handleToggle(v, ReceiptSettingsType.NOTES)} />
                <Toggle label="Show Sku" checked={settings.showSku} callback={v => handleToggle(v, ReceiptSettingsType.SKU)} />
                <Toggle
                    label="Show item description"
                    checked={settings.showDescription}
                    callback={v => handleToggle(v, ReceiptSettingsType.DESCRIPTION)}
                />
                <SelectInput
                    title="Show taxes"
                    options={taxesOptions}
                    value={getDropdownValue(ReceiptSettingsType.TAXES)}
                    callback={v => handleDropdown(v, ReceiptSettingsType.TAXES)}
                    layout="column"
                />
                <SelectInput
                    title="Show discounts"
                    options={discountsOptions}
                    value={getDropdownValue(ReceiptSettingsType.DISCOUNT)}
                    callback={v => handleDropdown(v, ReceiptSettingsType.DISCOUNT)}
                    layout="column"
                />
                <SelectInput
                    title="Show Fee"
                    options={cartFeeOptions}
                    value={getDropdownValue(ReceiptSettingsType.FEE)}
                    callback={v => handleDropdown(v, ReceiptSettingsType.FEE)}
                    layout="column"
                />
                <Toggle label="Show tips" checked={settings.showTips} callback={v => handleToggle(v, ReceiptSettingsType.TIPS)} />
                <Toggle label="Show signature" checked={settings.showSignature} callback={v => handleToggle(v, ReceiptSettingsType.SIGNATURE)} />
                {settings.showSignature && (
                    <>
                        <Input
                            label="Signature label"
                            value={node.signature?.label || ""}
                            callback={v => dispatch(updateNodeProperty({ propertyPath: ["signature", "label"], value: v }))}
                        />
                        <Input
                            label="Signature subtext"
                            value={node.signature?.subtext || ""}
                            callback={v => dispatch(updateNodeProperty({ propertyPath: ["signature", "subtext"], value: v }))}
                        />
                    </>
                )}
            </div>
        </SidebarCollapse>
    );
};

export default ReceiptData;
